import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaidIcon from '@mui/icons-material/Paid';
import FastForwardIcon from '@mui/icons-material/FastForward';
import { GitMerge as GitMergeIcon } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { PageHeaderAction } from '../../../../shared/components/layout/PageHeaderAction';
import { useCompanyDetailsMissing } from '../../../organisation/components/CompanyDetailsBanner';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface PotPageActionsProps {
  potDetails: PotDetailsDisplayModel;
  onApprove: () => void;
  onReject: () => void;
  onManageRejection: () => void;
  onMarkPaid: () => void;
  onMoveToActivePeriod: () => void;
  onMoveToPrimary: () => void;
}

export function PotPageActions({
  potDetails,
  onApprove,
  onReject,
  onManageRejection,
  onMarkPaid,
  onMoveToActivePeriod,
  onMoveToPrimary,
}: PotPageActionsProps) {
  const { status } = potDetails.approvals;
  const companyDetailsMissing = useCompanyDetailsMissing();
  const blockedTooltip = companyDetailsMissing
    ? 'Complete organisation details in Settings before approving or paying pots'
    : '';

  return (
    <>
      {status === 'Submitted' && (
        <>
          <PermissionGate permission={Permissions.Pots.Approve}>
            <Tooltip title={blockedTooltip}>
              <span>
                <PageHeaderAction
                  label="Approve"
                  icon={<CheckCircleIcon />}
                  onClick={onApprove}
                  disabled={companyDetailsMissing}
                  data-testid="approve-pot-button"
                />
              </span>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={Permissions.Pots.Reject}>
            <PageHeaderAction
              label="Reject"
              icon={<CancelIcon />}
              onClick={onReject}
              data-testid="reject-pot-button"
            />
          </PermissionGate>
        </>
      )}
      {status === 'Rejected' && (
        <PermissionGate permission={Permissions.Pots.Reject}>
          <PageHeaderAction
            label="Manage Rejection"
            icon={<CancelIcon />}
            onClick={onManageRejection}
            data-testid="manage-rejection-button"
          />
        </PermissionGate>
      )}
      {status === 'Approved' && (
        <PermissionGate permission={Permissions.Pots.Pay}>
          <Tooltip title={blockedTooltip}>
            <span>
              <PageHeaderAction
                label="Mark Paid"
                icon={<PaidIcon />}
                onClick={onMarkPaid}
                disabled={companyDetailsMissing}
                data-testid="mark-pot-paid-button"
              />
            </span>
          </Tooltip>
        </PermissionGate>
      )}
      {(status === 'MissedSubmitDeadline' ||
        status === 'MissedVerificationDeadline') && (
        <PermissionGate permission={Permissions.Pots.Move}>
          <PageHeaderAction
            label="Move to Active"
            icon={<FastForwardIcon />}
            onClick={onMoveToActivePeriod}
            data-testid="move-to-active-period-button"
          />
        </PermissionGate>
      )}
      {!potDetails.isPrimary &&
        status !== 'MissedSubmitDeadline' &&
        status !== 'MissedVerificationDeadline' && (
          <PermissionGate permission={Permissions.Pots.Move}>
            <PageHeaderAction
              label="Move to Primary"
              icon={<GitMergeIcon size={20} />}
              onClick={onMoveToPrimary}
              data-testid="move-to-primary-button"
            />
          </PermissionGate>
        )}
    </>
  );
}
